import { useState } from 'react'
import { Modal } from '@/components/Modal'
import { KNOWN_GOVERNANCE_PATH_TEMPLATES, type GovernancePathTemplate } from '../governancePathTemplates'

export interface AddGovernancePathModalProps {
  open: boolean
  onClose: () => void
  existingPaths: string[]
  onSelect: (path: string) => void
}

interface TemplateGroup {
  group: string
  templates: GovernancePathTemplate[]
}

function groupTemplates(templates: GovernancePathTemplate[]): TemplateGroup[] {
  const groups = new Map<string, TemplateGroup>()
  for (const template of templates) {
    const existing = groups.get(template.group)
    if (existing) existing.templates.push(template)
    else groups.set(template.group, { group: template.group, templates: [template] })
  }
  return Array.from(groups.values())
}

// Search + grouped-list picker, mirroring Client Overrides' AddOverrideModal
// (same Modal shell, same search-then-click-to-select, single-select closes
// immediately) - plus a "Custom path" fallback the per-firm picker doesn't
// need, since override_governance authoring must support a path this static
// template list doesn't know about yet.
export function AddGovernancePathModal({ open, onClose, existingPaths, onSelect }: AddGovernancePathModalProps) {
  const [search, setSearch] = useState('')
  const [customPath, setCustomPath] = useState('')

  const query = search.trim().toLowerCase()
  const available = KNOWN_GOVERNANCE_PATH_TEMPLATES.filter((template) => !existingPaths.includes(template.path))
  const filtered = available.filter((template) =>
    `${template.group} ${template.label} ${template.path}`.toLowerCase().includes(query),
  )
  const groups = groupTemplates(filtered)
  const trimmedCustomPath = customPath.trim()
  const isDuplicateCustomPath = trimmedCustomPath.length > 0 && existingPaths.includes(trimmedCustomPath)

  function handleClose() {
    setSearch('')
    setCustomPath('')
    onClose()
  }

  function handleSelect(path: string) {
    onSelect(path)
    handleClose()
  }

  function handleAddCustom() {
    if (trimmedCustomPath.length === 0 || isDuplicateCustomPath) return
    handleSelect(trimmedCustomPath)
  }

  return (
    <Modal open={open} title="Add allowlisted path" onClose={handleClose}>
      <div className="flex flex-col gap-3">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search known paths…"
          autoFocus
          className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
        <div className="flex max-h-[240px] flex-col gap-4 overflow-y-auto">
          {groups.length === 0 ? (
            <span className="text-meta text-text-disabled">No matching known paths - use Custom path below</span>
          ) : (
            groups.map((group) => (
              <div key={group.group} className="flex flex-col gap-1">
                <span className="px-2 text-meta font-semibold uppercase tracking-wide text-text-secondary">
                  {group.group}
                </span>
                {group.templates.map((template) => (
                  <button
                    key={template.path}
                    type="button"
                    onClick={() => handleSelect(template.path)}
                    className="flex flex-col items-start gap-0.5 rounded-control px-2 py-1.5 text-left hover:bg-surface"
                  >
                    <span className="text-body text-text-primary">{template.label}</span>
                    <span className="font-mono text-meta text-text-secondary">{template.path}</span>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="flex flex-col gap-1 border-t border-border pt-3">
          <label className="text-meta font-medium text-text-secondary" htmlFor="governance-custom-path">
            Custom path
          </label>
          <div className="flex gap-2">
            <input
              id="governance-custom-path"
              type="text"
              value={customPath}
              onChange={(event) => setCustomPath(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleAddCustom()
                }
              }}
              placeholder="e.g. product_types.*.limits.max_documents"
              className="flex-1 rounded-control border border-border bg-bg px-3 py-2 font-mono text-meta text-text-primary outline-none placeholder:text-text-disabled"
            />
            <button
              type="button"
              onClick={handleAddCustom}
              disabled={trimmedCustomPath.length === 0 || isDuplicateCustomPath}
              className="rounded-control bg-accent px-3 py-2 text-meta font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add
            </button>
          </div>
          {isDuplicateCustomPath && <span className="text-meta text-danger">That path is already in the list</span>}
        </div>
      </div>
    </Modal>
  )
}
